import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './style.module.css';

const WorkoutSelectionStep = ({
  onNext,
  onBack,
  selectedWorkouts: initialWorkouts = [],
  stepData,
}) => {
  const [selectedWorkouts, setSelectedWorkouts] = useState(
    Array.isArray(initialWorkouts) ? initialWorkouts : []
  );

  // Get workout options from API
  const workoutOptions =
    stepData?.options
      ?.map(option => ({
        id: option.value || option.label, // Use value as ID, fallback to label
        label: option.label,
        value: option.value,
        icon: option.mediaUrl,
        displayOrder: option.displayOrder,
      }))
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) || [];

  // Initialize selectedWorkouts from stepData if user has previously answered
  useEffect(() => {
    // Priority 1: Always check current session data first (user made changes in this session)
    // If session data exists (even if empty), it should take priority over API data
    if (
      initialWorkouts !== null &&
      initialWorkouts !== undefined &&
      Array.isArray(initialWorkouts)
    ) {
      // Filter out hubspotSkipValue from session data
      const filteredSessionData = initialWorkouts.filter(
        workout => workout !== stepData?.hubspotSkipValue
      );

      setSelectedWorkouts(filteredSessionData);
    }
    // Priority 2: Only use API data if no session data exists (browser refresh scenario only)
    else if (
      stepData?.hasAnswered &&
      stepData?.userResponse &&
      Array.isArray(stepData.userResponse)
    ) {
      // Check if userResponse contains only the hubspotSkipValue
      const isSkippedValue =
        stepData.userResponse.length === 1 &&
        stepData.userResponse[0] === stepData?.hubspotSkipValue;

      if (isSkippedValue) {
        // If it's a skipped value, set empty array (no selections)
        setSelectedWorkouts([]);
      } else {
        // Filter out hubspotSkipValue values
        const filteredUserResponse = stepData.userResponse.filter(
          value => value !== stepData?.hubspotSkipValue
        );

        const userResponseIds = filteredUserResponse.map(value => {
          // Find the workout option that has this value
          const matchingOption = workoutOptions.find(option => option.value === value);
          return matchingOption?.id || value; // Use ID if found, fallback to value
        });
        setSelectedWorkouts(userResponseIds);
      }
    }
  }, [
    stepData?.hasAnswered,
    stepData?.userResponse,
    stepData?.hubspotSkipValue,
    workoutOptions.length,
  ]); // Include workoutOptions.length to wait for API data

  const handleWorkoutToggle = workoutId => {
    setSelectedWorkouts(prevSelected => {
      // Ensure prevSelected is always an array
      const currentSelection = Array.isArray(prevSelected) ? prevSelected : [];

      // If already selected, remove it
      if (currentSelection.includes(workoutId)) {
        return currentSelection.filter(id => id !== workoutId);
      }

      // Get maxInput from API
      const maxInput = stepData?.validations?.maxInput;

      // If we already have maxInput selections and trying to add more, don't add
      if (maxInput && currentSelection.length >= maxInput) {
        return currentSelection;
      }

      // Otherwise, add it
      return [...currentSelection, workoutId];
    });
  };

  const handleNextClick = () => {
    if (onNext && selectedWorkouts.length > 0) {
      // Convert selected IDs to actual values for API submission
      const selectedValues = selectedWorkouts.map(workoutId => {
        const workoutObject = workoutOptions.find(workout => workout.id === workoutId);
        return workoutObject?.value || workoutId; // Use value from API, fallback to ID
      });

      onNext(selectedValues);
    }
  };

  const handleSkipClick = () => {
    if (onNext) {
      // Clear selections immediately for UI feedback
      setSelectedWorkouts([]);

      // Small delay to ensure state update is visible before navigation
      setTimeout(() => {
        // Use hubspotSkipValue from API if available, otherwise send empty array
        if (stepData?.hubspotSkipValue) {
          onNext([stepData.hubspotSkipValue]);
        } else {
          onNext([]);
        }
      }, 50);
    }
  };

  // Validation function based on API minInput/maxInput
  const isNextButtonEnabled = () => {
    const minInput = stepData?.validations?.minInput;
    const maxInput = stepData?.validations?.maxInput;

    const selectionCount = selectedWorkouts.length;

    // Check minimum requirement
    if (minInput > 0 && selectionCount < minInput) {
      return false;
    }

    // Check maximum requirement (though UI already handles this with disabled cards)
    if (maxInput && selectionCount > maxInput) {
      return false;
    }

    return true;
  };

  // Get dynamic maxInput value for rendering logic
  const maxInput = stepData?.validations?.maxInput;

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerContainer}>
        <h2 className={styles.header}>{stepData?.title}</h2>
        {stepData?.subtitle && <p className={styles.subHeader}>{stepData.subtitle}</p>}
      </div>

      <div className={styles.workoutsContainer}>
        {workoutOptions.map(workout => (
          <div
            key={workout.id}
            className={`${styles.workoutCard} ${
              selectedWorkouts.includes(workout.id) ? styles.selectedCard : ''
            } ${
              maxInput &&
              selectedWorkouts.length >= maxInput &&
              !selectedWorkouts.includes(workout.id)
                ? styles.disabledCard
                : ''
            }`}
            onClick={() => {
              // Only allow selection if card is not disabled
              if (
                !(
                  maxInput &&
                  selectedWorkouts.length >= maxInput &&
                  !selectedWorkouts.includes(workout.id)
                )
              ) {
                handleWorkoutToggle(workout.id);
              }
            }}
          >
            <div className={styles.workoutIcon}>
              <img
                src={workout.icon}
                alt={workout.label}
                className={
                  selectedWorkouts.includes(workout.id) ? styles.selectedIcon : ''
                }
              />
            </div>
            <span className={styles.workoutText}>{workout.label}</span>
          </div>
        ))}
      </div>

      <div className={styles.buttonsContainer}>
        <div className={styles.buttonGroup}>
          <button type="button" className={styles.skipButton} onClick={handleSkipClick}>
            Skip
          </button>
          <button
            type="button"
            className={styles.nextButton}
            disabled={!isNextButtonEnabled()}
            onClick={handleNextClick}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

WorkoutSelectionStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  selectedWorkouts: PropTypes.arrayOf(PropTypes.string),
  stepData: PropTypes.object,
};

WorkoutSelectionStep.defaultProps = {
  onBack: () => {},
  selectedWorkouts: [],
};

export default WorkoutSelectionStep;
